import React, { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  setObjectRolesPageNo,
  fetchObjectRolesList,
  deleteObjectRole,
  incrementObjectRolesPageNo,
  getObjectRoleById,
} from "./ducks";
import ObjectRolesModal from "./Modal/ObjectRolesModal";
import { TDispatch, TRootState } from "../../../redux/types";

import Drawer from "@components/Drawer";
import { OldInput } from "@components/OldInput";
import { Text } from "@components/Text";
import ExpandableRow from "@components/ExpandableRow";
import { Button } from "@components/Button";
import { Flex } from "reflexbox";
import { AddButton } from "@components/AddButton";
import { ScrollableList } from "@components/ScrollableList";
import { Column } from "@components/ExpandableRow/styles";
import { useSwal } from "@hooks/useSwal";
import { useHistory, useLocation } from "react-router-dom";
import { useFormatMessage } from "@hooks/formatMessage";

import { deserialize } from "../../../api/jsonApiParser";
import { getParameterByName } from "../../../utils/toSearchParams";

import { ReactComponent as IconFilter } from "../../../assets/icons/filter.svg";
import { ReactComponent as IconEdit } from "../../../assets/icons/edit.svg";
import { ReactComponent as IconTrash } from "../../../assets/icons/trash.svg";

import "../../../styles/index.scss";
import "./ObjectRoles.scss";

enum formMode {
  edit = "edit",
  password = "password",
}

const ObjectRoles: FC = () => {
  const [filtersOpened, setFiltersOpened] = useState(window.innerWidth >= 1400);
  const [modalContent, setModalContent] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(undefined);
  const [addObjectRoleForm, setAddObjectRoleForm] = useState(undefined);
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();
  const [showMoreSpinner, setShowMoreSpinner] = useState(false);
  const [objectRoleFilter, setObjectRoleFilter] = useState({
    GenericText: null,
  });

  const history = useHistory();
  const location = useLocation();
  const formatMessage = useFormatMessage();
  const dispatch: TDispatch = useDispatch();

  const { objectRolesList, moreObjectRoles, objectRolesPageNo } = useSelector(
    ({ ObjectRolesReducer: { objectRoles, moreObjectRoles, objectRolesPageNo } }: TRootState) => ({
      objectRolesList: objectRoles,
      moreObjectRoles,
      objectRolesPageNo,
    }),
    shallowEqual
  );

  if (location.search && location.search.length > 0) {
    setModalContent(getParameterByName("content", location.search));
    setModalIsOpen(!!modalContent);
  }

  useEffect(() => {
    dispatch(fetchObjectRolesList()).then(() => dispatch(incrementObjectRolesPageNo()));
  }, [dispatch]);

  const handleLoadMore = () => {
    if (!showMoreSpinner && moreObjectRoles) {
      setShowMoreSpinner(true);
      dispatch(fetchObjectRolesList(objectRolesPageNo, 10, objectRoleFilter)).then(() => {
        setShowMoreSpinner(false);
        dispatch(incrementObjectRolesPageNo());
      });
    }
  };

  const changeFilterOpened = () => {
    setFiltersOpened(prevState => !prevState);
  };

  const filterObjectRoles = () => {
    dispatch(setObjectRolesPageNo(1));
    dispatch(fetchObjectRolesList(1, 10, objectRoleFilter));
  };

  const clearFilters = () => {
    setObjectRoleFilter({
      GenericText: null,
    });

    dispatch(setObjectRolesPageNo(1));
    dispatch(fetchObjectRolesList(1, 10));
  };

  const openModal = (content, message?, entry?) => {
    if (content === formMode.edit) {
      getObjectRoleById(entry.id).then(ret => {
        if (ret) {
          setAddObjectRoleForm(deserialize(ret.data));
          setModalIsOpen(true);
          setModalContent(content);
        }
      });
    } else {
      setAddObjectRoleForm({});
      setModalIsOpen(true);
      setModalContent(content);
    }
  };

  const saveHistoryAndFilterObjectRoles = () => {
    history.push({
      search: "",
    });
    filterObjectRoles();
  };

  const closeModal = () => {
    setModalIsOpen(false);
    saveHistoryAndFilterObjectRoles();

    // TODO: this.props.fetchCompanies().then(res => this.setState({ parentCompanyList: res }));
    // TODO: change the location of the list to the modal component
  };

  const showSuccessMessage = (action, callbackFunction) => {
    swalAlertWithCallback(
      {
        text: `Object Role has been ${action}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      callbackFunction
    );
  };

  const remove = id => {
    swalConfirmationMessage({
      text: "Do you really want to remove this object role?",
      type: "info",
      cancelationText: "Cancel",
      confirmationText: "Remove object role",
    }).then(value => {
      if (value) {
        deleteObjectRole(id).then(ret => {
          if (ret) showSuccessMessage("removed", saveHistoryAndFilterObjectRoles);
        });
      }
    });
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Large Modal"
        className={"large-modal"}
        overlayClassName={"large-modal-overlay"}
      >
        {modalIsOpen && modalContent !== formMode.password && (
          <ObjectRolesModal closeModal={closeModal} initialData={addObjectRoleForm} />
        )}
      </Modal>

      <Flex pl={["10px", "80px"]} pr={["10px", 0]} height="100%" width="100%">
        <Flex flexDirection="column" width="100%">
          <AddButton
            id="addObjectRoleBtn"
            p="0 32px"
            m="32px 0"
            label={formatMessage("app.addnewobjectrole", "Add Object Role")}
            onClick={() => {
              setAddObjectRoleForm({});
              setModalIsOpen(true);
            }}
          />
          <ScrollableList
            items={objectRolesList}
            onLoadMore={handleLoadMore}
            renderHeader={
              <>
                <Column>
                  <Text>{formatMessage("app.objectName", "Object Name")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.roleName", "Role Name")}</Text>
                </Column>
                <Column icon>
                  <Text>
                    <div
                      id="toggleFilterArea"
                      className="icon-filter"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation();
                        changeFilterOpened();
                      }}
                    >
                      <IconFilter />
                    </div>
                  </Text>
                </Column>
              </>
            }
            renderItem={(item, index) => (
              <ExpandableRow
                data-cy="objectRoleRow"
                key={"objectRoleRow" + index}
                id={item.id as string}
                translucent={false}
                expandable={false}
                top={
                  <React.Fragment>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.objectName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.roleName}
                        </Text>
                      </div>
                    </Column>
                    <Column icon>
                      <div className="d-flex flex-row showChildOnHover">
                        <div
                          data-cy="changeEntry"
                          className="iconWrapper pointer large"
                          title="Edit Object Role"
                          style={{}}
                          onClick={() => openModal(formMode.edit, "", item)}
                        >
                          <IconEdit />
                        </div>
                        <div
                          data-cy="deleteEntry"
                          className="iconWrapper pointer large"
                          title="Delete Object Role"
                          style={{}}
                          onClick={() => remove(item.id)}
                        >
                          <IconTrash />
                        </div>
                      </div>
                    </Column>
                  </React.Fragment>
                }
              />
            )}
          />
        </Flex>
        <Drawer id="filterArea" opened={filtersOpened}>
          <div className="paddingContainer">
            <p className="colorGrey mb-1">Dynamic Filter</p>
            <div data-cy="dynamicFilterByField">
              <OldInput
                id="dynamicSearch"
                name="dynamicSearch"
                key="dynamicSearch"
                inputWrapperClass="text-input filter-input"
                placeholder={formatMessage("app.anyfield", "Any Field")}
                value={objectRoleFilter.GenericText}
                onChange={(name, value) =>
                  setObjectRoleFilter(prevState => ({ ...prevState, GenericText: value }))
                }
                validationMessageConfig={null /* this.validationMessageConfig() */}
                type="text"
                inputWrapperStyle={{}}
                filtering
              />
            </div>

            <div className="breaker mb-5" />

            <div className="modal-buttons">
              <Button id="buttonClearSearch" textButton mr="10px" onClick={clearFilters}>
                {formatMessage("app.clear", "Clear")}
              </Button>

              <Button id="buttonSearch" textButton mr="10px" onClick={filterObjectRoles}>
                {formatMessage("app.search", "Search")}
              </Button>
            </div>
          </div>
        </Drawer>
      </Flex>
    </>
  );
};

export default ObjectRoles;
